import { useListDeletion } from "@/components/settings/lists/useListDeletion";
import { useEffect, useRef, useState } from "react";
import { useLocation, useRoute } from "wouter";
import { Bell, Hourglass, Plus, Repeat, Trash2, X } from "lucide-react";
import AppShell from "@/components/foundations/core/AppShell/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { trpc } from "@/lib/trpc";
import {
  DEFAULT_AREA_COLOR,
  AREA_COLORS,
  ListColorKey,
  resolvePairedColorKeys,
  resolveInheritedListColorKeys,
  resolveTaskColors,
} from "@/lib/listColors";
import {
  BackgroundColorPicker,
  BarColorOverridePicker,
  ColorPreviewCard,
} from "@/components/foundations/inputs/ColorPicker/ColorPicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import TaskDetailsHeader from "@/components/tasks/details/TaskDetailsHeader/TaskDetailsHeader";
import PatternOptionsView from "@/components/tasks/options/PatternOptionsView/PatternOptionsView";
import PatternDisplayView from "@/components/tasks/options/PatternDisplayView/PatternDisplayView";
import PatternSupportPreview from "@/components/tasks/options/PatternSupportPreview/PatternSupportPreview";
import TaskInfoSection from "@/components/tasks/details/TaskInfoSection/TaskInfoSection";
import TaskFieldsForm, {
  TaskFieldsValue,
} from "@/components/tasks/details/TaskFieldsForm/TaskFieldsForm";
import TaskScheduleForm, {
  parseScheduleDuration,
} from "@/components/tasks/schedule/TaskScheduleForm/TaskScheduleForm";
import { createTaskScheduleValue } from "@/lib/taskTiming";
import { formatDurationShort } from "@/lib/time";
import { toast } from "sonner";
import ListSelector, {
  type ListOption,
} from "@/components/foundations/lists/ListSelector/ListSelector";
import {
  DEFAULT_TASK_VISIBLE_FIELDS,
  normalizeTaskVisibleFields,
  type TaskVisibleFields,
} from "@shared/taskVisibleFields";

type Repeats = "endTime" | "count";
type PatternView = "main" | "options" | "display";

const emptyForm: TaskFieldsValue = {
  title: "",
  details: "",
  // Notes are intentionally undefined — templates don't have notes.
  startTime: "",
  durationMin: "",
  alertEnabled: true,
  priority: null,
  repeat: {
    enabled: false,
    intervalMin: "60",
    endType: "endTime",
    endTime: "",
    count: "",
  },
  customColors: false,
  barColor: null,
  bgColor: null,
};

export default function ListEditor() {
  const [, params] = useRoute<{ id: string }>("/settings/lists/:id");
  const [, setLocation] = useLocation();
  const listId = params ? parseInt(params.id, 10) : 0;
  const utils = trpc.useUtils();
  const deletion = useListDeletion();

  const listQuery = trpc.lists.get.useQuery(
    { id: listId },
    { enabled: listId > 0 }
  );
  const categoriesQuery = trpc.categories.list.useQuery();
  const listsQuery = trpc.lists.list.useQuery();
  const templatesQuery = trpc.templates.list.useQuery(
    { listId },
    { enabled: listId > 0 }
  );

  // List-level edits
  const [name, setName] = useState("");
  const [categoryId, setCategoryId] = useState<string>("none");
  const [description, setDescription] = useState("");
  // Background is the PRIMARY color; bar follows bg unless explicitly overridden.
  const [bg, setBg] = useState<ListColorKey>(DEFAULT_AREA_COLOR);
  const [bgAuto, setBgAuto] = useState(true);
  const [barOverride, setBarOverride] = useState<ListColorKey | null>(null);

  useEffect(() => {
    if (listQuery.data && categoriesQuery.data) {
      const list = listQuery.data;
      const category = categoriesQuery.data.find(
        item => item.id === list.categoryId
      );
      setName(list.name);
      setCategoryId(list.categoryId ? String(list.categoryId) : "none");
      setDescription(list.description ?? "");
      const dbBg = (list.bgColor as ListColorKey | null) ?? null;
      const dbBar = (list.color as ListColorKey | null) ?? null;
      const inherited = resolveInheritedListColorKeys({
        primaryOverride: dbBar,
        secondaryOverride: dbBg,
        categoryPrimary: category?.color,
        categorySecondary: category?.bgColor,
      });
      const effectiveBg = dbBg ?? inherited.secondary;
      setBg(effectiveBg);
      setBgAuto(dbBg === null);
      setBarOverride(dbBar);
    }
  }, [listQuery.data, categoriesQuery.data]);

  const updateArea = trpc.lists.update.useMutation({
    onSuccess: () => {
      utils.lists.list.invalidate();
      utils.categories.list.invalidate();
      utils.lists.get.invalidate({ id: listId });
      toast.success("List updated");
    },
    onError: error => {
      setCategoryId(
        listQuery.data?.categoryId ? String(listQuery.data.categoryId) : "none"
      );
      toast.error(error.message || "Could not update list");
    },
  });

  // Template dialog state
  const [editingTemplateId, setEditingTemplateId] = useState<number | null>(
    null
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [patternListPickerOpen, setPatternListPickerOpen] = useState(false);
  const [patternView, setPatternView] = useState<PatternView>("main");
  const [patternDisplayReturnView, setPatternDisplayReturnView] =
    useState<PatternView>("main");
  const patternViewScrollRef = useRef<HTMLDivElement>(null);
  const [patternListId, setPatternListId] = useState(listId);
  const [patternDate, setPatternDate] = useState("");
  const [savedPatternInfo, setSavedPatternInfo] = useState({
    title: "",
    description: "",
  });
  const [form, setForm] = useState<TaskFieldsValue>(emptyForm);
  const [visibleFields, setVisibleFields] = useState<TaskVisibleFields>(
    DEFAULT_TASK_VISIBLE_FIELDS
  );

  function openPatternDisplay(returnView: PatternView) {
    setPatternDisplayReturnView(returnView);
    setPatternView("display");
  }
  useEffect(() => {
    patternViewScrollRef.current?.scrollTo({ top: 0, left: 0 });
  }, [patternView]);
  function openCreate() {
    setEditingTemplateId(null);
    setPatternListId(listId);
    setPatternDate("");
    setSavedPatternInfo({ title: "", description: "" });
    setPatternListPickerOpen(false);
    setPatternView("main");
    setPatternDisplayReturnView("main");
    setForm(emptyForm);
    setVisibleFields(DEFAULT_TASK_VISIBLE_FIELDS);
    setDialogOpen(true);
  }

  useEffect(() => {
    if (!listQuery.data || dialogOpen || typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const templateId = Number(params.get("pattern"));
    if (params.get("new") === "pattern") {
      openCreate();
    } else if (templateId > 0) {
      const template = templatesQuery.data?.find(
        item => item.id === templateId
      );
      if (template) openEdit(template);
    }
  }, [listQuery.data, dialogOpen, templatesQuery.data]);

  function openEdit(t: NonNullable<typeof templatesQuery.data>[number]) {
    setEditingTemplateId(t.id);
    setPatternListId(t.listId);
    setPatternDate("");
    setSavedPatternInfo({ title: t.title, description: t.details ?? "" });
    setPatternListPickerOpen(false);
    setPatternView("main");
    setPatternDisplayReturnView("main");
    const tBar = (t.barColor ?? null) as ListColorKey | null;
    const tBg = (t.bgColor ?? null) as ListColorKey | null;
    setVisibleFields(normalizeTaskVisibleFields(t.defaultVisibleFields));
    setForm({
      title: t.title,
      details: t.details ?? "",
      startTime: t.defaultStartTime ?? "",
      durationMin: t.defaultDurationMin?.toString() ?? "",
      alertEnabled: t.defaultAlertEnabled,
      priority: null,
      repeat: {
        enabled: t.defaultRepeatEnabled,
        intervalMin: t.defaultRepeatIntervalMin?.toString() ?? "60",
        endType: (t.defaultRepeatEndType as Repeats) ?? "endTime",
        endTime: t.defaultRepeatEndTime ?? "",
        count: t.defaultRepeatCount?.toString() ?? "",
      },
      customColors: tBar !== null || tBg !== null,
      barColor: tBar,
      bgColor: tBg,
    });
    setDialogOpen(true);
  }

  const createTemplate = trpc.templates.create.useMutation({
    onSuccess: () => {
      utils.templates.list.invalidate();
      utils.lists.list.invalidate();
      setDialogOpen(false);
      setPatternListPickerOpen(false);
      toast.success("Pattern added to list");
    },
    onError: error => toast.error(error.message),
  });
  const updateTemplate = trpc.templates.update.useMutation({
    onSuccess: () => {
      utils.templates.list.invalidate();
      utils.lists.list.invalidate();
      setDialogOpen(false);
      setPatternListPickerOpen(false);
      toast.success("Pattern updated");
    },
    onError: error => toast.error(error.message),
  });
  const removeTemplate = trpc.templates.remove.useMutation({
    onSuccess: () => {
      utils.templates.list.invalidate({ listId });
      utils.lists.list.invalidate();
    },
  });

  function buildPayload() {
    const intInput = (v: string) =>
      v.trim() === "" ? null : Math.max(1, parseInt(v, 10) || 1);
    const r = form.repeat!;
    return {
      title: form.title.trim(),
      details: form.details.trim() || undefined,
      defaultStartTime: form.startTime || null,
      defaultDurationMin: intInput(form.durationMin),
      defaultRepeatEnabled: r.enabled,
      defaultRepeatIntervalMin: r.enabled ? intInput(r.intervalMin) : null,
      defaultRepeatEndType: r.enabled ? r.endType : null,
      defaultRepeatEndTime:
        r.enabled && r.endType === "endTime" ? r.endTime || null : null,
      defaultRepeatCount:
        r.enabled && r.endType === "count" ? intInput(r.count) : null,
      defaultAlertEnabled: form.alertEnabled,
      defaultVisibleFields: visibleFields,
      barColor: form.customColors ? form.barColor : null,
      bgColor: form.customColors ? form.bgColor : null,
    };
  }

  function handleCommit(
    partial: Partial<TaskFieldsValue>,
    field: keyof TaskFieldsValue
  ) {
    // Normalize and persist individual field changes (e.g., when clear button is clicked).
    // This mirrors the pattern in TaskDetail.tsx.
    const payload: Record<string, unknown> = {};
    if (field === "title") {
      const next = (partial.title ?? "").trim();
      if (next === "") return; // title is required; ignore clear
      if (next !== form.title) payload.title = next;
    }
    if (field === "details") {
      const trimmed = (partial.details ?? "").trim();
      const next = trimmed === "" ? null : trimmed;
      if ((next ?? null) !== (form.details ?? "")) payload.details = next;
    }
    if (field === "startTime") {
      const next = partial.startTime ?? "";
      if (next !== form.startTime) payload.defaultStartTime = next || null;
    }
    if (field === "durationMin") {
      const raw = (partial.durationMin ?? "").trim();
      const next = raw === "" ? null : Math.max(1, parseInt(raw, 10) || 1);
      if (next !== (form.durationMin ? parseInt(form.durationMin, 10) : null)) {
        payload.defaultDurationMin = next;
      }
    }
    if (field === "alertEnabled") {
      if (partial.alertEnabled !== form.alertEnabled) {
        payload.defaultAlertEnabled = partial.alertEnabled;
      }
    }
    if (Object.keys(payload).length > 0 && editingTemplateId !== null) {
      updateTemplate.mutate({
        id: editingTemplateId,
        listId: patternListId,
        ...payload,
      });
    }
  }

  function handleScheduleChange(
    next: ReturnType<typeof createTaskScheduleValue>,
    reason:
      | "date"
      | "start-time"
      | "duration"
      | "alert"
      | "today"
      | "now"
      | "clear"
  ) {
    setPatternDate(next.timing.startDate);
    const nextForm = {
      ...form,
      startTime: next.timing.startTime,
      durationMin: next.timing.durationMinutes,
      alertEnabled: next.alertEnabled,
    };
    setForm(nextForm);
    if (reason === "start-time" || reason === "now") {
      handleCommit({ startTime: nextForm.startTime }, "startTime");
    } else if (reason === "alert") {
      handleCommit({ alertEnabled: nextForm.alertEnabled }, "alertEnabled");
    } else if (reason === "clear") {
      handleCommit(
        {
          startTime: nextForm.startTime,
          durationMin: nextForm.durationMin,
          alertEnabled: nextForm.alertEnabled,
        },
        "startTime"
      );
    }
  }

  function handleScheduleCommit(
    next: ReturnType<typeof createTaskScheduleValue>
  ) {
    const duration = parseScheduleDuration(next.timing.durationMinutes);
    if (
      duration !== (form.durationMin ? parseInt(form.durationMin, 10) : null)
    ) {
      handleCommit({ durationMin: next.timing.durationMinutes }, "durationMin");
    }
  }

  function handleClone() {
    const title = `${form.title.trim()} (clone)`.slice(0, 120);
    if (!title.trim()) return;
    createTemplate.mutate(
      { listId: patternListId, ...buildPayload(), title },
      { onSuccess: () => setDialogOpen(false) }
    );
  }
  function handleDelete() {
    if (editingTemplateId === null) return;
    if (!window.confirm(`Delete "${form.title || "this pattern"}"?`)) return;
    removeTemplate.mutate(
      { id: editingTemplateId },
      { onSuccess: () => setDialogOpen(false) }
    );
  }
  function handleSubmit() {
    const payload = buildPayload();
    if (!payload.title) return;
    if (editingTemplateId === null) {
      createTemplate.mutate({ listId: patternListId, ...payload });
    } else {
      updateTemplate.mutate({
        id: editingTemplateId,
        listId: patternListId,
        ...payload,
      });
    }
  }

  if (!listId) return null;

  const selectedCategory = (categoriesQuery.data ?? []).find(
    category => String(category.id) === categoryId
  );
  const selectedPatternArea = (listsQuery.data ?? []).find(
    list => list.id === patternListId
  );
  const patternHeaderColors = resolveTaskColors({
    barColor: form.customColors ? form.barColor : null,
    bgColor: form.customColors ? form.bgColor : null,
    listColor: selectedPatternArea?.color ?? DEFAULT_AREA_COLOR,
    listBgColor: selectedPatternArea?.bgColor ?? null,
  });
  const areaDisplayColors = resolveInheritedListColorKeys({
    primaryOverride: barOverride,
    secondaryOverride: bgAuto ? null : bg,
    categoryPrimary: selectedCategory?.color,
    categorySecondary: selectedCategory?.bgColor,
  });
  const c = AREA_COLORS[areaDisplayColors.primary];
  const isInboxArea = listQuery.data?.kind === "inbox";

  return (
    <AppShell backTo="/settings/lists" title={listQuery.data?.name ?? "List"}>
      <div className="container max-w-3xl py-8 space-y-10">
        {/* List details */}
        <section className="space-y-5">
          <div className="flex items-center gap-3">
            <span
              className="h-10 w-10 rounded-full flex items-center justify-center font-display"
              style={{ background: c.soft, color: c.ink }}
            >
              {listQuery.data?.priorityNumber ?? "—"}
            </span>
            <h1 className="font-display text-3xl tracking-tight">List</h1>
            {!isInboxArea && listQuery.data && (
              <Button
                className="ui-delete-button ml-auto"
                variant="destructive"
                disabled={deletion.isPending}
                onClick={() =>
                  void deletion.remove(
                    "list",
                    listId,
                    listQuery.data!.name,
                    () => setLocation("/settings/lists")
                  )
                }
              >
                <Trash2 className="h-4 w-4" /> Delete list
              </Button>
            )}
          </div>

          <div className="space-y-4 bg-card border border-border/60 rounded-xl p-5">
            <div className="space-y-2">
              <Label htmlFor="ln">Name</Label>
              <Input
                id="ln"
                value={name}
                disabled={isInboxArea}
                onChange={e => setName(e.target.value)}
                onBlur={() => {
                  if (
                    !isInboxArea &&
                    name.trim() &&
                    name !== listQuery.data?.name
                  ) {
                    updateArea.mutate({ id: listId, name: name.trim() });
                  }
                }}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select
                value={categoryId}
                onValueChange={value => {
                  setCategoryId(value);
                  if (!isInboxArea) {
                    updateArea.mutate({
                      id: listId,
                      categoryId: value === "none" ? null : Number(value),
                    });
                  }
                }}
                disabled={isInboxArea}
              >
                <SelectTrigger id="category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No category</SelectItem>
                  {(categoriesQuery.data ?? []).map(category => (
                    <SelectItem key={category.id} value={String(category.id)}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="ld">Description</Label>
                {description && (
                  <button
                    type="button"
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => {
                      setDescription("");
                      if (listQuery.data?.description) {
                        updateArea.mutate({ id: listId, description: null });
                      }
                    }}
                    className="inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                    aria-label="Clear description"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <Textarea
                id="ld"
                value={description}
                rows={2}
                onChange={e => setDescription(e.target.value)}
                onBlur={() => {
                  if (description !== (listQuery.data?.description ?? "")) {
                    updateArea.mutate({
                      id: listId,
                      description: description.trim() || null,
                    });
                  }
                }}
              />
            </div>
            <BarColorOverridePicker
              value={barOverride}
              bgColor={selectedCategory?.color ?? areaDisplayColors.secondary}
              onChange={k => {
                setBarOverride(k);
                updateArea.mutate({
                  id: listId,
                  color: k,
                });
              }}
              label="Primary color"
            />
            <BackgroundColorPicker
              value={bgAuto ? null : bg}
              onChange={k => {
                const nextBg = k ?? barOverride ?? bg;
                setBgAuto(k === null);
                setBg(nextBg);
                updateArea.mutate({
                  id: listId,
                  bgColor: k,
                });
              }}
              label="Secondary color"
              showAuto
              autoColor={
                selectedCategory?.bgColor ??
                selectedCategory?.color ??
                areaDisplayColors.primary
              }
            />
            <div className="space-y-1.5 pt-1">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">
                Preview
              </p>
              <ColorPreviewCard
                barColor={barOverride}
                bgColor={bgAuto ? null : bg}
                fallbackBar={selectedCategory?.color ?? DEFAULT_AREA_COLOR}
                fallbackBg={
                  selectedCategory?.bgColor ??
                  selectedCategory?.color ??
                  DEFAULT_AREA_COLOR
                }
                title={name.trim() || "Sample task"}
                label={name.trim() || "List"}
              />
            </div>
          </div>
        </section>

        {!isInboxArea && (
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-display text-2xl tracking-tight">
                  Patterns
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Patterns are reoccurring templates for how you work.
                </p>
              </div>
              <Button size="sm" onClick={openCreate} className="gap-1.5">
                <Plus className="h-4 w-4" />
                New pattern
              </Button>
            </div>

            {templatesQuery.isLoading ? (
              <div className="space-y-2">
                {[0, 1].map(i => (
                  <div
                    key={i}
                    className="h-16 rounded-xl bg-muted/40 animate-pulse"
                  />
                ))}
              </div>
            ) : (templatesQuery.data?.length ?? 0) === 0 ? (
              <div className="border border-dashed border-border rounded-xl p-10 text-center">
                <p className="text-sm text-muted-foreground">
                  Add a pattern to get started.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {templatesQuery.data!.map(t => {
                  const resolved = resolveTaskColors({
                    barColor: t.barColor as string | null | undefined,
                    bgColor: t.bgColor as string | null | undefined,
                    listColor: barOverride ?? bg,
                    listBgColor: bg,
                  });
                  const tc = {
                    base: resolved.bar,
                    soft: resolved.soft,
                    ink: resolved.ink,
                  };
                  return (
                    <div
                      key={t.id}
                      className="group relative border border-border/60 hover:border-border rounded-xl overflow-hidden transition-all"
                      style={{ background: tc.soft }}
                    >
                      <div className="flex items-stretch">
                        <span
                          className="w-1.5"
                          style={{ background: tc.base }}
                          aria-hidden
                        />
                        <div className="flex-1 flex items-start gap-3 px-4 py-3 min-w-0">
                          <button
                            onClick={() => openEdit(t)}
                            className="flex-1 min-w-0 text-left"
                          >
                            {/* Row 1: time (bold) · list name (light) */}
                            <div className="flex items-baseline gap-2">
                              <span
                                className="text-xs font-semibold tabular-nums font-mono shrink-0"
                                style={{ color: tc.ink }}
                              >
                                {t.defaultStartTime || "—"}
                              </span>
                              <span
                                className="text-[11px] uppercase tracking-[0.14em] truncate"
                                style={{ color: tc.ink, opacity: 0.55 }}
                              >
                                {listQuery.data?.name ?? "List"}
                              </span>
                            </div>

                            {/* Row 2: title */}
                            <div
                              className="font-semibold text-base mt-1"
                              style={{ color: tc.ink }}
                            >
                              {t.title}
                            </div>

                            {/* Description */}
                            {t.details && (
                              <div
                                className="text-sm mt-1 truncate"
                                style={{ color: tc.ink, opacity: 0.75 }}
                              >
                                {t.details}
                              </div>
                            )}

                            {/* Icons */}
                            <div
                              className="flex items-center gap-3 mt-2 text-[11px] tabular-nums font-mono"
                              style={{ color: tc.ink, opacity: 0.7 }}
                            >
                              {t.defaultAlertEnabled && (
                                <span
                                  className="inline-flex items-center gap-1"
                                  title="Alerts on"
                                >
                                  <Bell className="h-3 w-3" />
                                </span>
                              )}
                              {t.defaultRepeatEnabled && (
                                <span
                                  className="inline-flex items-center gap-1"
                                  title="Repeats"
                                >
                                  <Repeat className="h-3 w-3" />
                                </span>
                              )}
                              {t.defaultDurationMin && (
                                <span
                                  className="inline-flex items-center gap-1"
                                  title="Duration"
                                >
                                  <Hourglass className="h-3 w-3" />
                                  {formatDurationShort(t.defaultDurationMin)}
                                </span>
                              )}
                            </div>
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm(`Delete "${t.title}"?`)) {
                                removeTemplate.mutate({ id: t.id });
                              }
                            }}
                            className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-destructive/10 hover:text-destructive transition-colors"
                            style={{ color: tc.ink, opacity: 0.65 }}
                            aria-label="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editingTemplateId === null ? "New pattern" : "Edit pattern"}
            </DialogTitle>
          </DialogHeader>

          <div
            ref={patternViewScrollRef}
            className="ui-pattern-editor-viewport min-h-0 flex-1 overflow-x-hidden overflow-y-auto"
          >
            <div
              className="ui-pattern-editor-track flex w-full transition-transform duration-300 ease-[cubic-bezier(0.23,1,0.32,1)]"
              style={{
                transform:
                  patternView === "main"
                    ? "translateX(0%)"
                    : patternView === "options"
                      ? "translateX(-100%)"
                      : "translateX(-200%)",
              }}
            >
              <div className="w-full shrink-0 space-y-3 py-2">
                <TaskDetailsHeader
                  listName={
                    selectedPatternArea?.name ?? listQuery.data?.name ?? "Inbox"
                  }
                  colors={patternHeaderColors}
                  onAreaClick={() => setPatternListPickerOpen(true)}
                  onOptionsClick={() => setPatternView("options")}
                />
                <TaskInfoSection
                  value={{ title: form.title, description: form.details }}
                  savedValue={savedPatternInfo}
                  colors={patternHeaderColors}
                  onChange={next => {
                    setForm(current => ({
                      ...current,
                      title: next.title,
                      details: next.description,
                    }));
                  }}
                  onCommit={(next, reason) => {
                    if (reason === "title") {
                      handleCommit({ title: next.title }, "title");
                    } else {
                      handleCommit({ details: next.description }, "details");
                    }
                  }}
                  onDisplayClick={() => openPatternDisplay("main")}
                />
                <TaskScheduleForm
                  value={createTaskScheduleValue({
                    date: patternDate,
                    startTime: form.startTime,
                    durationMinutes: form.durationMin,
                    alertEnabled: form.alertEnabled,
                  })}
                  onChange={handleScheduleChange}
                  onCommit={handleScheduleCommit}
                  open
                  alwaysOpen
                  onOpenChange={() => undefined}
                  colors={patternHeaderColors}
                  emptyLabel="Default schedule"
                />
                <TaskFieldsForm
                  value={form}
                  onChange={setForm}
                  onCommit={handleCommit}
                  sections={["repeat"]}
                  labels={{
                    previewLabel:
                      selectedPatternArea?.name ??
                      listQuery.data?.name ??
                      "List",
                    fallbackBar:
                      selectedPatternArea?.color ?? DEFAULT_AREA_COLOR,
                    fallbackBg: selectedPatternArea?.bgColor ?? null,
                  }}
                  accent={patternHeaderColors}
                />

                <PatternSupportPreview
                  visibleFields={visibleFields}
                  colors={patternHeaderColors}
                />
              </div>
              <div className="ui-pattern-editor-subviews w-full shrink-0">
                {patternView === "options" && (
                  <PatternOptionsView
                    title={form.title}
                    details={form.details}
                    listName={
                      selectedPatternArea?.name ??
                      listQuery.data?.name ??
                      "Inbox"
                    }
                    colors={patternHeaderColors}
                    visibleFields={visibleFields}
                    repeatRule={
                      form.repeat?.enabled
                        ? {
                            kind: "interval",
                            unit: "minute",
                            every: Number(form.repeat.intervalMin || 60),
                            end: { kind: "count", count: 1 },
                          }
                        : { kind: "none" }
                    }
                    onToggleVisibility={field =>
                      setVisibleFields(current => ({
                        ...current,
                        [field]: !current[field],
                      }))
                    }
                    onOpenDisplay={() => openPatternDisplay("options")}
                    onClone={handleClone}
                    onDelete={handleDelete}
                    onBack={() => setPatternView("main")}
                  />
                )}
              </div>
              <div className="ui-pattern-editor-subviews w-full shrink-0">
                {patternView === "display" && (
                  <PatternDisplayView
                    title={form.title}
                    listName={
                      selectedPatternArea?.name ??
                      listQuery.data?.name ??
                      "Inbox"
                    }
                    colors={patternHeaderColors}
                    value={{
                      inheritView: !form.customColors,
                      barColor: form.barColor,
                      bgColor: form.bgColor,
                    }}
                    fallbackBar={
                      selectedPatternArea?.color ?? DEFAULT_AREA_COLOR
                    }
                    fallbackBg={selectedPatternArea?.bgColor ?? null}
                    onChange={next =>
                      setForm(current => ({
                        ...current,
                        customColors: !next.inheritView,
                        barColor: next.barColor,
                        bgColor: next.bgColor,
                      }))
                    }
                    onBack={() => setPatternView(patternDisplayReturnView)}
                  />
                )}
              </div>
            </div>
          </div>
          {patternListPickerOpen && (
            <ListSelector
              lists={(listsQuery.data ?? []) as ListOption[]}
              value={patternListId}
              onChange={nextListId => {
                if (nextListId === null) return;
                setPatternListId(nextListId);
                if (editingTemplateId !== null) {
                  updateTemplate.mutate(
                    { id: editingTemplateId, listId: nextListId },
                    {
                      onSuccess: () => {
                        setPatternListPickerOpen(false);
                        setDialogOpen(true);
                      },
                    }
                  );
                } else {
                  setPatternListPickerOpen(false);
                }
              }}
              onClose={() => setPatternListPickerOpen(false)}
              title="Move pattern to list"
            />
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={
                !form.title.trim() ||
                createTemplate.isPending ||
                updateTemplate.isPending
              }
              onClick={handleSubmit}
            >
              {editingTemplateId === null ? "Add pattern" : "Save pattern"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
